import { startTransition, useEffect, useRef } from "react";
import DataDumpGetter from "../../../segments/DataDumpGetter/DataDumpGetter";
import type {
  DataDumpAggregateType,
  TimeRangeFinite,
} from "../../../types/types";
import {
  useFocusRange,
  useInterval,
  useShouldApplyShiftRangesToDataDumps,
  useVisibleGroups,
} from "../../../utils/customHooks/genericHooks";
import { useDispatch } from "react-redux";
import {
  UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES,
  UPDATE_MANY_DATA_DUMP_TREND_CHART_COUNTS_FOR_PROCESSES,
} from "providers/GlobalStateProvider";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useCleanRawDataDump } from "segments/DataDumpGetter/dataDumpGetterUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import {
  useRangeFilteredByShift,
  useTrendBarQueryRangesFromFocusRange,
} from "./TrendChartBarsGetter";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useFilteredShifts } from "utils/customHooks/trendChartHooks";

// * this TrendChartBarsCountsDataDumpsGetter is only used if flightConfig.trendChart.overrideCountMachemetryIdsPerGroup is specified

/** renders one getter per trend chart bar
 *
 * rendered OUTSIDE of the trend chart bar itself to avoid rerender loops
 */
const TrendChartBarsCountsDataDumpsGetter = () => {
  const [flightConfig] = useFlightConfig();
  const processGroup = useVisibleGroups()[0];
  const overrideCountMachemetryIds =
    !processGroup || !flightConfig.trendChart.overrideCountMachemetryIdsPerGroup
      ? null
      : (flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.shortName
        ] ??
        flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.longName
        ] ??
        null);
  return overrideCountMachemetryIds ? (
    <TrendChartBarsCountsDataDumpsGetterContent />
  ) : null;
};

const TrendChartBarsCountsDataDumpsGetterContent = () => {
  const {
    trendChartBarsRanges: trendChartBarsRangesFromFocusRange,
    updatingNow,
  } = useTrendBarQueryRangesFromFocusRange();
  let trendChartBarsRanges = trendChartBarsRangesFromFocusRange;

  // on trends page, account for filtered-by-shift state
  const shouldApplyShiftRangesToDataDumps =
    useShouldApplyShiftRangesToDataDumps();
  const shiftRanges = useFilteredShifts().map(shift => shift.range);
  if (shouldApplyShiftRangesToDataDumps) {
    trendChartBarsRanges = shiftRanges;
  }

  const [flightConfig] = useFlightConfig();
  const processGroup = useVisibleGroups()[0];
  const overrideCountMachemetryIds =
    !processGroup || !flightConfig.trendChart.overrideCountMachemetryIdsPerGroup
      ? null
      : (flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.shortName
        ] ??
        flightConfig.trendChart.overrideCountMachemetryIdsPerGroup[
          processGroup.longName
        ] ??
        []);
  const processIds = overrideCountMachemetryIds ?? [];

  // dispatching the aggregates to the store causes a major re-render of many components.
  // so, to stabilize the render, we'll only dispatch once, when all the data is ready.
  const trendChartBarsDataDumpsRef = useRef<{
    bars: DataDumpAggregateType[];
    lastUpdate: number;
  }>({
    lastUpdate: Date.now(),
    bars: [],
  });
  // empty the bars whenever time range or process changes (these bars are now stale)
  const processIdOnSingleProcessPage = useProcessIdOnSingleProcessPage();
  const focusRange = useFocusRange();
  useEffect(() => {
    trendChartBarsDataDumpsRef.current = {
      bars: [],
      lastUpdate: Date.now(),
    };
  }, [focusRange, processIdOnSingleProcessPage]);
  const dispatch = useDispatch();
  const cleanRawDataDump = useCleanRawDataDump();
  useInterval({
    callback: () => {
      // check on an interval if we're ready to dispatch yet
      const allBarsPresent =
        trendChartBarsDataDumpsRef.current.bars.length ===
        trendChartBarsRanges.length;
      const newBarsPresent = trendChartBarsDataDumpsRef.current.bars.length > 0;
      const timeElapsed =
        Date.now() - trendChartBarsDataDumpsRef.current.lastUpdate;
      const ready =
        allBarsPresent || (newBarsPresent && timeElapsed > 6 * 1000);
      if (ready) {
        startTransition(() => {
          dispatch({
            type: UPDATE_MANY_DATA_DUMP_TREND_CHART_COUNTS_FOR_PROCESSES,
            payload: trendChartBarsDataDumpsRef.current.bars.map(dataDump =>
              cleanRawDataDump(dataDump, processIds)
            ),
          });
          trendChartBarsDataDumpsRef.current = {
            ...trendChartBarsDataDumpsRef.current,
            bars: [],
          };
        });
      }
    },
    delay: 2 * 1000,
    immediate: false,
  });
  const currentTrendBarRanges = trendChartBarsRanges.filter(
    range => range.ge < updatingNow
  );

  // restart the process when we change focus range (otherwise, we might dispatch some bars from a different focus range into the current focus range)

  return processIds.length === 0 ? null : (
    <>
      {currentTrendBarRanges.map(range => {
        // if it's the current bar, we'll refetch (re-render) whenever updatingNow updates
        const isCurrentBar = range.ge <= updatingNow && updatingNow < range.lt;
        return (
          <DataDumpGetterTrendChartBar
            // only use ge since lt on the current bar changes
            key={`${range.ge}-${JSON.stringify(processIds)}-${
              isCurrentBar ? updatingNow : ""
            }`}
            range={range}
            processIds={processIds}
            trendChartBarsDataDumpsRef={trendChartBarsDataDumpsRef}
          />
        );
      })}
    </>
  );
};

export default TrendChartBarsCountsDataDumpsGetter;

function DataDumpGetterTrendChartBar({
  range,
  processIds,
  trendChartBarsDataDumpsRef,
}: {
  range: TimeRangeFinite;
  processIds: string[];
  trendChartBarsDataDumpsRef: React.MutableRefObject<{
    bars: DataDumpAggregateType[];
    lastUpdate: number;
  }>;
}) {
  const rangeAfterFilteredByShift = useRangeFilteredByShift(range);
  const isStorybook = useIsStorybook();

  return isStorybook ? null : (
    <>
      <DataDumpGetter
        timeRange={{
          ge: rangeAfterFilteredByShift.ge,
          lt: rangeAfterFilteredByShift.lt,
        }}
        processIds={processIds}
        dataDumpAggType={UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES}
        retryUntilReceivedInterval={30 * 1000}
        customOnCompleted={data => {
          trendChartBarsDataDumpsRef.current = {
            ...trendChartBarsDataDumpsRef.current,
            bars: [
              ...(trendChartBarsDataDumpsRef.current.bars || []),
              data.dataDumpAggregate,
            ],
            lastUpdate: Date.now(),
          };
        }}
      />
    </>
  );
}
